import {useContext} from 'react'

import PropTypes from 'prop-types'

import Badge, {atomBadgeDesigns, atomBadgeSizes, atomBadgeTypes} from '@s-ui/react-atom-badge'
import Context from '@s-ui/react-context'
import PolymorphicElement from '@s-ui/react-primitive-polymorphic-element'
import CardAdPrice from '@adv-mt/mt-card-ad-price'
import LabelPriceDrop from '@adv-mt/mt-label-price-drop'
import RatingBadge from '@adv-mt/mt-rating-badge'
import CarCertified from '@adv-mt/mt-svg-icons/lib/CarCertified'
import IconLocation from '@adv-mt/mt-svg-icons/lib/Location'

import CardAdAttributes from './card-ad-attributes.js'
import CardAdInfoHeader from './card-ad-info-header.js'

const TYPES = {
  HAS_STOCK: 'aquaWithoutHover',
  ACTUALIDAD: 'teal',
  IS_FINANCED: 'blue',
  IS_URGENT: 'bloodOrange',
  MULTIPROVINCE: 'aquaWithoutHover',
  HAS_RESERVATION: 'aquaWithoutHover',
  HAS_ONLINE_FINANCING: 'aquaWithoutHover',
  DEFAULT: 'gray'
}

const CardAdInfo = ({
  ad,
  cardAdFavProps,
  extendedTrackingData,
  financingData = {},
  forcedShowPriceIndicator,
  hasMenu,
  isDeleted,
  isDiscardedAdsEnabled,
  isGridLayout = false,
  isPreview,
  linkFactory,
  multiprovince,
  onAfterDiscard = () => {},
  showFavorite,
  showIsNewInformation,
  showPriceDrop,
  showTimeAgoInformation,
  timeAgoInformation,
  trackingData,
  vertical
}) => {
  const {i18n, domain, user} = useContext(Context)
  const {isSubscription} = user || {}
  const config = domain.get('config')
  const provinces = config.get('provinces')
  const {
    specificFueltypes,
    fuelTypes,
    OFFER_TYPE_NEW,
    OFFER_TYPE_KM0,
    OFFER_TYPE_SUBSTITUTION,
    OFFER_TYPE_MANAGEMENT,
    OFFER_TYPE_DEMONSTRATION,
    TAGS_VISIBILITY,
    transmissionTypes = [],
    SHOW_MULTI_PROVINCE_INFO_ADS
  } = config.get('search')
  const {VEHICLE_STATE_IDS} = config.get('subscription') || {}

  const offerTypesWithTag = [
    OFFER_TYPE_NEW,
    OFFER_TYPE_KM0,
    OFFER_TYPE_SUBSTITUTION,
    OFFER_TYPE_MANAGEMENT,
    OFFER_TYPE_DEMONSTRATION
  ]

  // In coches `specificFueltypes` was created to include different fuel types
  // such as electrico and more! We do not have this in motos, so we use a fallback
  const _fuelTypes = specificFueltypes || fuelTypes
  const {
    environmentalLabel,
    hasStock,
    hp,
    isCertified,
    isFinanced,
    isSubscription: isSubscriptionAd,
    km,
    location,
    offerType,
    powerHp,
    priceDropData = {},
    provinceId,
    specificFuelTypeId,
    subscriptionVehicleState,
    transmissionTypeId,
    year
  } = ad

  const hasReservation = Boolean(ad?.hasReservation)

  const dealerRatingValue = ad?.seller?.ratings?.average

  const hasMpLocation = Boolean(location) && location.provinceIds.length > 1

  const _getProvinceName = () => {
    const mainProvinceId = hasMpLocation ? location.provinceIds[0] : provinceId
    const province = provinces.find(province => province.id === mainProvinceId)

    const showLocation = SHOW_MULTI_PROVINCE_INFO_ADS && hasMpLocation

    return province
      ? {
          icon: showLocation && IconLocation,
          id: `card-ad-info-location-icon-${ad.id}`,
          key: 'province',
          title: i18n.t('COMMON.LOCATION'),
          value: province.label
        }
      : null
  }

  const _getFueltype = () => {
    const fuelType = _fuelTypes.find(fuelType => fuelType.id === specificFuelTypeId)
    return fuelType ? i18n.t(fuelType.label) : null
  }

  const _getPowerHp = () => {
    return powerHp ? `${powerHp} ${i18n.t('COMMON.UNIT_HP')}` : undefined
  }

  const _getHp = () => {
    return hp ? `${hp} ${i18n.t('COMMON.UNIT_HP')}` : undefined
  }

  const _getTransmissionType = () => {
    const [transmissionType] = transmissionTypes.filter(({id}) => Number(id) === Number(transmissionTypeId))
    return transmissionType ? i18n.t(transmissionType.label) : undefined
  }

  const _getKm = () => {
    // Without an explicit culture, SSR formats with Node's default locale (`7,840`) while the browser
    // uses the user's (`7840`). React 18's concurrent hydration throws on that text mismatch instead
    // of patching it, which tears down the whole listing page.
    return km ? `${km.toLocaleString(i18n.culture)} km` : undefined
  }

  const _getOfferTypeTag = () => {
    const hasOfferType = Boolean(offerType)

    if (!hasOfferType) return

    const isTag = offerTypesWithTag.filter(Boolean).some(offerTypeIdTag => offerType.id === offerTypeIdTag)

    return isTag ? offerType.literal : undefined
  }

  const hasOfferType = Boolean(offerType)
  const isNewVehicle = hasOfferType && offerType.id === OFFER_TYPE_NEW
  const showKmAttribute = !isSubscriptionAd && !isNewVehicle && !isSubscription

  const offerTypeTag = _getOfferTypeTag()

  const hasStockTag = (isNewVehicle || isSubscriptionAd) && hasStock
  const hasMultiProvinceTag = TAGS_VISIBILITY.MULTI_PROVINCE && hasMpLocation
  const hasIsFinancedTag = TAGS_VISIBILITY.FINANCED && isFinanced
  const hasOfferTypeTag = Boolean(offerTypeTag)
  const hasSubscriptionVehicleStateNewTag = isSubscriptionAd && subscriptionVehicleState === VEHICLE_STATE_IDS.NEW
  const hasSubscriptionVehicleStateUsedTag = isSubscriptionAd && subscriptionVehicleState === VEHICLE_STATE_IDS.USED
  const hasRatingsTag = Boolean(ad?.seller?.ratings)

  const showProvinceAttribute = !isPreview && !isSubscriptionAd

  const attributes = [
    {key: 'environmentalLabel', value: environmentalLabel},
    {key: 'fuelType', value: _getFueltype()},
    {key: 'year', value: year},
    isSubscriptionAd && {
      // only show transmission type on subscription ads
      key: 'transmissionType',
      value: _getTransmissionType()
    },
    showKmAttribute && {key: 'km', value: _getKm()},
    {key: 'hp', value: _getHp()},
    isSubscriptionAd && {
      // only show horse power on subscription ads
      key: 'powerHp',
      value: _getPowerHp()
    },
    showProvinceAttribute && {..._getProvinceName()}
  ].filter(Boolean)

  const {amount: priceDropAmount} = priceDropData
  const hasPriceDropData = Boolean(priceDropAmount)
  const showPriceFootNote = isSubscriptionAd

  const instalmentPrice = ad?.financingInfo?.instalment || financingData?.fee

  const tagsInluded = [
    hasStockTag,
    hasMultiProvinceTag,
    hasIsFinancedTag,
    hasOfferTypeTag,
    hasSubscriptionVehicleStateNewTag,
    hasSubscriptionVehicleStateUsedTag,
    hasRatingsTag,
    hasReservation
  ].filter(Boolean)

  const hasTags = Boolean(tagsInluded.length)

  const hasSingleTag = tagsInluded.length === 1

  const hasCertifiedBadge = isCertified && !isNewVehicle

  const showOverImageBadges = hasCertifiedBadge || showIsNewInformation || showTimeAgoInformation

  return (
    <>
      <CardAdInfoHeader
        ad={ad}
        extendedTrackingData={extendedTrackingData}
        hasMenu={hasMenu}
        isDeleted={isDeleted}
        isPreview={isPreview}
        cardAdFavProps={cardAdFavProps}
        isDiscardedAdsEnabled={isDiscardedAdsEnabled}
        linkFactory={linkFactory}
        onAfterDiscard={onAfterDiscard}
        showFavorite={showFavorite}
        trackingData={trackingData}
      />
      {showOverImageBadges && (
        <div className="mt-CardAd-overImageBadges">
          {showIsNewInformation && (
            <Badge
              design={atomBadgeDesigns.SOLID}
              label={i18n.t('SEARCH_SAVE_ONBOARDING_LABEL_NEW')}
              size={atomBadgeSizes.LARGE}
              type={atomBadgeTypes.NEUTRAL}
            />
          )}
          {showTimeAgoInformation && (
            <Badge
              design={atomBadgeDesigns.SOLID}
              label={timeAgoInformation}
              size={atomBadgeSizes.LARGE}
              type={atomBadgeTypes.NEUTRAL}
            />
          )}
          {hasCertifiedBadge && (
            <Badge icon={<CarCertified />} label={i18n.t('COMMON_CERTIFIED')} size={atomBadgeSizes.LARGE} />
          )}
        </div>
      )}
      {showPriceDrop && hasPriceDropData && (
        <div className="mt-CardAd-priceDropWrapper">
          <LabelPriceDrop amount={priceDropAmount} />
        </div>
      )}
      <CardAdPrice
        ad={ad}
        forcedShowPriceIndicator={forcedShowPriceIndicator}
        hasFootNote={showPriceFootNote}
        instalmentPrice={instalmentPrice}
        isGridLayout={isGridLayout}
        isPreview={isPreview}
        vertical={vertical}
      />
      <CardAdAttributes id={ad.id} attributes={attributes} isVertical={vertical} />
      {/* The single-tag container is a `div`, not a `p`: the tags hold badges that render `div`s, and
          the HTML parser closes an open `p` when it meets one, so the server markup would parse into a
          different tree than React renders and hydration of the whole page would fail. */}
      {hasTags && (
        <PolymorphicElement as={hasSingleTag ? 'div' : 'ul'} className="mt-CardAd-tags">
          {hasReservation && (
            <PolymorphicElement as={hasSingleTag ? 'span' : 'li'} className="mt-CardAd-tag">
              <Badge
                design={atomBadgeDesigns.SOFT}
                label={'Reservable'}
                type={TYPES.HAS_RESERVATION}
                size={atomBadgeSizes.MEDIUM}
              />
            </PolymorphicElement>
          )}
          {hasMultiProvinceTag && (
            <PolymorphicElement as={hasSingleTag ? 'span' : 'li'} className="mt-CardAd-tag">
              <Badge
                design={atomBadgeDesigns.SOFT}
                label={i18n.t('COMMON.FLEXIBLE_DELIVERY')}
                type={TYPES.MULTIPROVINCE}
                size={atomBadgeSizes.MEDIUM}
              />
            </PolymorphicElement>
          )}
          {hasSubscriptionVehicleStateNewTag && (
            <PolymorphicElement as={hasSingleTag ? 'span' : 'li'} className="mt-CardAd-tag">
              <Badge
                design={atomBadgeDesigns.SOFT}
                label={i18n.t('COMMON.OFFER_TYPE.NEW')}
                type={TYPES.DEFAULT}
                size={atomBadgeSizes.MEDIUM}
              />
            </PolymorphicElement>
          )}
          {hasOfferTypeTag && (
            <PolymorphicElement as={hasSingleTag ? 'span' : 'li'} className="mt-CardAd-tag">
              <Badge
                design={atomBadgeDesigns.SOFT}
                label={offerTypeTag}
                type={TYPES.DEFAULT}
                size={atomBadgeSizes.MEDIUM}
              />
            </PolymorphicElement>
          )}
          {hasStockTag && (
            <PolymorphicElement as={hasSingleTag ? 'span' : 'li'} className="mt-CardAd-tag">
              <Badge
                design={atomBadgeDesigns.SOFT}
                label={i18n.t('COMMON.HAS_STOCK')}
                type={TYPES.DEFAULT}
                size={atomBadgeSizes.MEDIUM}
              />
            </PolymorphicElement>
          )}
          {hasSubscriptionVehicleStateUsedTag && (
            <PolymorphicElement as={hasSingleTag ? 'span' : 'li'} className="mt-CardAd-tag">
              <Badge
                design={atomBadgeDesigns.SOFT}
                label={i18n.t('COMMON.PRE_OWNED')}
                type={TYPES.DEFAULT}
                size={atomBadgeSizes.MEDIUM}
              />
            </PolymorphicElement>
          )}
          {hasRatingsTag && (
            <PolymorphicElement as={hasSingleTag ? 'span' : 'li'} className="mt-CardAd-tag">
              <RatingBadge id={ad.id} value={dealerRatingValue} />
            </PolymorphicElement>
          )}
          {hasIsFinancedTag && (
            <PolymorphicElement as={hasSingleTag ? 'span' : 'li'} className="mt-CardAd-tag">
              <Badge
                design={atomBadgeDesigns.SOFT}
                label={i18n.t('COMMON.FINANCED')}
                type={TYPES.IS_FINANCED}
                size={atomBadgeSizes.MEDIUM}
              />
            </PolymorphicElement>
          )}
        </PolymorphicElement>
      )}
    </>
  )
}

CardAdInfo.displayName = 'CardAdInfo'

CardAdInfo.propTypes = {
  ad: PropTypes.object.isRequired,
  cardAdFavProps: PropTypes.object,
  extendedTrackingData: PropTypes.object,
  financingData: PropTypes.object,
  financingInfo: PropTypes.object,
  forcedShowPriceIndicator: PropTypes.bool,
  hasMenu: PropTypes.bool,
  isDeleted: PropTypes.bool,
  isDiscardedAdsEnabled: PropTypes.bool,
  isGridLayout: PropTypes.bool,
  isPreview: PropTypes.bool,
  linkFactory: PropTypes.func,
  multiprovince: PropTypes.string,
  onAfterDiscard: PropTypes.func,
  showFavorite: PropTypes.bool,
  showIsNewInformation: PropTypes.bool,
  showPriceDrop: PropTypes.bool,
  showTimeAgoInformation: PropTypes.bool,
  timeAgoInformation: PropTypes.string,
  trackingData: PropTypes.object,
  vertical: PropTypes.bool
}

export default CardAdInfo
